import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutContent, siteInfo } from '@/mocks/appData';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于CPP - 品牌介绍与联系方式',
    description: '了解CPP的品牌故事、核心价值观、发展历程与联系方式。CPP致力于为全球用户提供安全高效纯净的移动应用体验。',
    url: 'https://lytfzs.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://lytfzs.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <ScrollToTop />
      <main>
        <Navbar />
        <div className="pt-24 pb-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              关于我们
            </h1>
            <p className="text-sm text-foreground-600 mb-10">了解CPP的品牌故事与发展历程</p>

            {/* Brand Intro */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-20">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {aboutContent.intro.title}
                </h2>
                {aboutContent.intro.content.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-sm text-foreground-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={aboutContent.intro.image}
                  alt="CPP办公环境"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </section>

            {/* Mission */}
            <section className="mb-20 py-12 px-8 rounded-2xl bg-primary-500 text-center">
              <div className="max-w-[700px] mx-auto">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-50/20 mb-4">
                  <i className="ri-focus-2-line text-2xl text-background-50"></i>
                </div>
                <p className="text-base md:text-lg text-background-50 leading-relaxed font-heading">
                  {aboutContent.mission}
                </p>
              </div>
            </section>

            {/* Values */}
            <section className="mb-20">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutContent.values.map((value) => (
                  <div
                    key={value.id}
                    className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center"
                  >
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-check-double-line text-xl"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">
                      {value.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Milestones */}
            <section className="mb-20">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                发展历程
              </h2>
              <div className="max-w-[700px] mx-auto">
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-background-200"></div>
                  <div className="space-y-8">
                    {aboutContent.milestones.map((milestone, index) => (
                      <div key={index} className="relative pl-12">
                        <div className="absolute left-1.5 top-1 w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                          {String(index + 1)}
                        </div>
                        <div className="inline-block px-2.5 py-1 text-xs font-medium whitespace-nowrap rounded-full bg-primary-100 text-primary-700 mb-1.5">
                          {milestone.year}
                        </div>
                        <p className="text-sm text-foreground-700">{milestone.event}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section id="contact" className="mb-4">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: aboutContent.contact.email },
                    { icon: 'ri-phone-line', label: '客服电话', value: aboutContent.contact.phone },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: aboutContent.contact.address },
                    { icon: 'ri-wechat-line', label: '微信公众号', value: aboutContent.contact.wechat },
                    { icon: 'ri-weibo-line', label: '官方微博', value: aboutContent.contact.weibo },
                    { icon: 'ri-time-line', label: '服务时间', value: aboutContent.contact.hours },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className="p-4 rounded-xl bg-background-100 border border-background-200/70"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                          <i className={item.icon}></i>
                        </div>
                        <span className="text-xs font-medium text-foreground-500">{item.label}</span>
                      </div>
                      <p className="text-sm text-foreground-800 font-medium">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">
                    给我们留言
                  </h3>
                  <form
                    data-readdy-form
                    id="contact-form"
                    className="space-y-4"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget as HTMLFormElement;
                      const antiValue = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                      if (antiValue) {
                        const msg = document.getElementById('form-feedback');
                        if (msg) {
                          msg.textContent = '留言已提交，我们会尽快回复！';
                          msg.className = 'text-xs text-accent-600 mt-3';
                        }
                        form.reset();
                        return;
                      }
                      try {
                        const formData = new FormData(form);
                        const res = await fetch('https://readdy.ai/api/form/d9q9racnlsngrm4llu7g', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const text = await res.text();
                        const msg = document.getElementById('form-feedback');
                        if (msg) {
                          if (res.ok) {
                            msg.textContent = '感谢您的留言，我们会尽快回复！';
                            msg.className = 'text-xs text-accent-600 mt-3';
                            form.reset();
                          } else {
                            msg.textContent = '提交失败，请稍后重试';
                            msg.className = 'text-xs text-red-500 mt-3';
                          }
                        }
                      } catch {
                        const msg = document.getElementById('form-feedback');
                        if (msg) {
                          msg.textContent = '网络错误，请稍后重试';
                          msg.className = 'text-xs text-red-500 mt-3';
                        }
                      }
                    }}
                  >
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言内容..."
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-anti-bot"
                    />
                    <button
                      type="submit"
                      className="w-full py-3 text-sm font-semibold whitespace-nowrap rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200"
                    >
                      提交留言
                    </button>
                    <div id="form-feedback" className="text-xs text-accent-600 mt-3"></div>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}